import { createModel } from '@rematch/core';
import apiConnector from '../../api/ApiConnector';
import {
    IBodyPart, IExpertChangeRequestId, IFilterPart,
} from 'components/models';
import {
    ApproveExpertChangeRequest,
    ExpertChangeRequestDetails,
    ExpertChangeRequests,
    ExpertChangeRequestStatus, IApproveExpertChangeRequest, IRejectExpertChangeRequest,
    ISendCodeForApproveExpertChangeRequest, RejectExpertChangeRequest,
    SendCodeForApproveExpertChangeRequest,
    VerificationCodeSendingStatus,
} from '../../api/ApiClient';
import { IFullSearchPart, IIdFilter, IFromToDatePart } from 'components/reduxModels';

const initialState: IState = {
    expertChangeRequests: null,
    details: null,
    smsStatus: null,
    historyExpertChanges: null,
};

export default createModel({
    state: initialState,
    reducers: {
        setExpertChangeRequests(state: IState, expertChangeRequests: ExpertChangeRequests | null) {
            return { ...state, expertChangeRequests };
        },
        clearExpertChangeRequests(state: IState) {
            return { ...state, expertChangeRequests: null };
        },
        setHistoryExpertChangeRequests(state: IState, historyExpertChanges: IState['historyExpertChanges']) {
            return { ...state, historyExpertChanges };
        },
        setDetails(state: IState, details: ExpertChangeRequestDetails | null) {
            return { ...state, details };
        },
        clearDetails(state: IState) {
            return { ...state, details: null };
        },
        setSmsStatus(state: IState, smsStatus: VerificationCodeSendingStatus | null) {
            return { ...state, smsStatus };
        },
        clearSmsStatus(state: IState) {
            return { ...state, smsStatus: null };
        },
    },
    effects: (dispatch: any) => ({
        async getExpertChangeRequest(id: string) {
            const changeRequest = await apiConnector.execApi(dispatch, api => api.getExpertChangeRequest(id));
            this.setDetails(changeRequest);
        },
        async getCurrentExpertChangeRequestsAsync(payload: IPayloadCurrentExpertChangeRequests) {
            const expertChangeRequests = await apiConnector.execApi(dispatch, apiClient => apiClient.getCurrentExpertChangeRequests(
              payload.expertIds,
              payload.from,
              payload.to,
              payload.status,
                undefined,
              payload.page,
              payload.pageSize,
              payload.sortOrder,
              payload.sortField,
            ));
            if (!expertChangeRequests) return;
            this.setExpertChangeRequests(expertChangeRequests);
        },

        async sendCodeForApproveExpertChangeRequest(payload: IExpertChangeRequestId & IBodyPart<ISendCodeForApproveExpertChangeRequest>) {
            const smsStatus = await apiConnector.execApi(dispatch, api => api.sendCodeForApproveExpertChangeRequest(
              payload.expertChangeRequestId,
              new SendCodeForApproveExpertChangeRequest(payload.body),
            ));
            this.setSmsStatus(smsStatus);
        },

        async approveExpertChangeRequestAsync(payload: IExpertChangeRequestId & IBodyPart<IApproveExpertChangeRequest>) {
            return apiConnector.execApi(dispatch, api => api.approveExpertChangeRequest(
              payload.expertChangeRequestId,
              new ApproveExpertChangeRequest(payload.body),
            ));
        },

        async rejectExpertChangeRequest(payload: IExpertChangeRequestId & IBodyPart<IRejectExpertChangeRequest>) {
            return apiConnector.execApi(dispatch, api => api.rejectExpertChangeRequest(
              payload.expertChangeRequestId,
              new RejectExpertChangeRequest(payload.body),
            ));
        },

        async getExpertChangeRequestsHistory(payload: IIdFilter & IFullSearchPart & IFromToDatePart) {
            this.setHistoryExpertChangeRequests(await apiConnector.execApi(dispatch, api => api.getExpertChangeRequestsHistory(
              payload.id,
              payload.from,
              payload.to,
              payload.page,
              payload.pageSize,
              payload.sortOrder,
              payload.sortField,
            )));
        },
    }),
});

interface IPayloadCurrentExpertChangeRequests extends IFromToDatePart, IFilterPart {
    expertIds?: string[];
    status?: ExpertChangeRequestStatus | undefined;
}

interface IState {
    expertChangeRequests: ExpertChangeRequests | null;
    details: ExpertChangeRequestDetails | null;
    smsStatus: VerificationCodeSendingStatus | null;
    historyExpertChanges: ExpertChangeRequests | null;
}
